(ns nosco.util.diff
    "Utilities to generate a \"unified\" diff of two idea templates.
     This namespace is only dealing with vectors of ids, to detect added,
     removed or moved elements. The actual change detection about properties
     of template items is not dealt with here."
  (:require [lambdaisland.deep-diff2 :as ddiff]
            [medley.core :as medley]
            [clojure.set]))


(defn- unwrap-impl
  "Convert a namespaced keyword to a plain one"
  [m]
  (medley/map-keys #(keyword (name %)) m))

(comment
  ;; ddiff provides a single vector where the edits are represented by records
  ;; in our case since we assume that the input is always keywords or strings,
  ;; we can use a map? predicate to detect edits.
  (ddiff/diff [:title :description :area :dept] [:title :area :problem :description :division])
  ;; => [:title
  ;;     {:- :description}
  ;;     :area
  ;;     {:- :dept, :+ :problem}
  ;;     {:+ :description}
  ;;     {:+ :division}]


  ;; using sets we can find which elements are the same between the two templates,
  ;; and thus detect elements that are moved
  (clojure.set/intersection  (set [:title :description :area :dept])
                             (set [:title :area :problem :description :division]))
  ;; => #{:description :title :area}


)

(defn- diff-ids
  "Given two vectors of ids, return a vector of
     {:id <an id>
     :type :removal|addition|nil}

     A moved element will be represented as removal/addition"
  [from-ids to-ids]
  (let [diff (ddiff/diff from-ids to-ids)]
    (loop [new []
           diff diff]
      (let [op (first diff)]
        (cond
          ;; termination
          (nil? op)
          new

          ;; a ddiff map with :+ , :- or both
          ;; (actually, namespaced records so we unwrap them)
          (map? op)
          (let [{:keys [- +]} (unwrap-impl op)]
            (cond
              ;; when both are present, expand them one by one
              (and - +)
              (recur (conj new {:id + :type :addition})
                     (cons {:- -} (rest diff)))

              ;; otherwise just convert
              + (recur (conj new {:id + :type :addition})
                       (rest diff))
              - (recur (conj new {:id - :type :removal})
                       (rest diff))))

          ;; no change, so just push it
          :else
          (recur (conj new {:id op :type nil})
                 (rest diff)))))))

(defn layout-ids
  "Given two vectors of ids, return a vector of
     {:id <an id>
     :type :removal|addition|move|nil}
   that represents the unified diff."
  [from-ids to-ids]
  (let [same (clojure.set/intersection (set from-ids) (set to-ids))
        diff (diff-ids (vec from-ids) (vec to-ids))]
    (loop [new []
           diff diff]
      (let [op (first diff)
            {:keys [id type]} op]
        (if (nil? op)
          ;; termination
          new

          ;; since ddiff doesn't deal with moves, and
          ;; expresses them as a removal+addition,
          ;; we postprocess them here, by keeping track of
          ;; the elements that are kept are in the `same` set
          ;; we elide the removal and convert the addition into a
          ;; :move operation
          (case type
            nil
            (recur (conj new op) (rest diff))

            :addition
            (if-not (contains? same id)
              (recur (conj new op) (rest diff))
              (recur (conj new (assoc op :type :move)) (rest diff)))

            :removal
            (if-not (contains? same id)
              (recur (conj new op) (rest diff))
              (recur new (rest diff)))))))))


(defn merge-elements
  "Given a base vector of elements, and a new version of elements,
   use the layout algorithm to merge the two together, keeping the order
   as much as possible. Only additions are considered, no moves or removals.
   It is assumed that the elements contain an id field at :id"
  [base new]
  (let [layout (layout-ids (mapv :id base)
                           (mapv :id new))
        additions (for [[idx {:keys [id type]}] (map-indexed vector layout)
                        :when (= :addition type)
                        :let [element (first (filter #(= id (:id %)) new))]]
                    {:idx idx
                     :element element})]
    (reduce (fn [acc {:keys [idx element]}]
              (medley/insert-nth idx element acc))
            base
            additions)))

(comment
  (merge-elements [{:id 1 :title "foo"} {:id 4 :title "new"} {:id 2 :title "bar"}]
                  [{:id 1 :title "foo"} {:id 2 :title "bar"} {:id 3 :title "bam"}])

  )

#_
(defn layout-ids-alternative [from-ids to-ids]
  (let [from-ids-set (set from-ids)
        to-ids-set (set to-ids)]
    (loop [from-ids from-ids
           to-ids to-ids
           changes []]
      (let [from-id (first from-ids)
            to-id (first to-ids)]
        (cond (and (nil? from-id) (nil? to-id))
              changes
              (= from-id to-id)
              (recur (next from-ids)
                     (next to-ids)
                     (conj changes {:id to-id
                                    :type nil}))
              :else
              (let [addition? (and to-id (not (contains? from-ids-set to-id)))
                    move? (and to-id (contains? from-ids-set to-id))
                    removal? (and from-id
                                  (not (contains? to-ids-set from-id)))]
                (recur (if (or removal? (nil? to-id)) (next from-ids) from-ids)
                       (next to-ids)
                       (cond-> changes
                         addition? (conj {:id to-id
                                          :type :addition})
                         move? (conj {:id to-id
                                      :type :move})
                         removal? (conj {:id from-id
                                         :type :removal})))))))))
